import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import {
  usePlayerPreviewMutation,
  useTracksQuery,
  useUpdateTrackMutation,
  useUpdateTracksMutation,
} from '../../api/api'
import { cloneDeep, set } from 'lodash'
import PortfolioRegionSettings from './PortfolioRegionSettings'
import TracksView from '../TracksView/TracksView'
import PlaylistEditor from '../../components/PlaylistEditor/PlaylistEditor'
import TrackView from '../TrackView/TrackView'
import BulkTrackView from '../TrackView/BulkTrackView'
import { ReactComponent as GearIcon } from 'assets/icons/gear.svg'
import PlaylistEditModal from '../PlaylistView/PlaylistEditModal/PlaylistEditModal'
import Modal from 'react-modal/lib/components/Modal'

const PortfolioPlaylistEditor = ({ values, onChange, isPreview }) => {
  const { data: tracksData } = useTracksQuery()
  const [updateTrack, { isLoading: updateTrackIsLoading }] =
    useUpdateTrackMutation()
  const [updateTracks, { isLoading: updateTracksIsLoading }] =
    useUpdateTracksMutation()
  const [getPlayerPreview] = usePlayerPreviewMutation()

  const ref = useRef()
  const [tracksToEdit, setTracksToEdit] = useState([])
  const [playlistSettings, setPlaylistSettings] = useState(null)
  const pageData = values?.pageData || {}
  const containerStyle = {
    justifyContent: pageData?.playlistRegion?.verticalAlign || 'flex-start',
  }

  const theme = pageData?.theme || 'dark'
  const playlistRegion = pageData.playlistRegion || {}
  const playerSettings = {
    ...(playlistRegion.playerSettings || {}),
    theme,
  }
  const playlistNode = values?.playlistData?.data?.find(
    (item) => item?.id?.slice(0, 1) === 'p',
  )

  const { data: tracks } = tracksData || []

  useEffect(() => {
    if (ref.current && isPreview) {
      fetchPlayer()
    }
  }, [theme, isPreview])

  useEffect(() => {
    if (tracks?.length > 0) {
      const playlistData = values.playlistData?.data
      const newData = playlistData.map((item) => {
        const newItem = cloneDeep(item)
        const trackId = item?.data?.trackId
        if (trackId) {
          const track = tracks.find((track) => track.id === trackId)
          if (track) {
            newItem.text = track.name
          }
        }
        return newItem
      })
      handlePlaylistChange({ ...values.playlistData, data: newData })
    }
  }, [tracks])

  async function fetchPlayer() {
    const styleTags = [
      ...document.querySelectorAll('style[data-player-uuid="preview"]'),
    ]
    const div = document.createElement('div')
    const script = document.createElement('script')
    const preview = await getPlayerPreview({
      data: values.playlistData?.data,
      styles: playlistNode?.data?.theme,
    })

    div.id = 'preview'
    script.type = 'text/javascript'
    script.text = preview?.data?.script
    div.appendChild(script)
    ref.current.replaceChildren(div)
    styleTags.forEach((tag) => tag.remove())
  }

  function handlePlaylistChange(data) {
    onChange({
      ...values,
      playlistData: data,
    })
  }

  function handlePlaylistSettingsChange(data, { publish }) {
    const newValues = cloneDeep(values)
    const playlistNodeIndex = newValues.playlistData.data?.findIndex(
      (node) => node.id.slice(0, 1) === 'p',
    )
    if (playlistNodeIndex !== -1) {
      const playlist = newValues.playlistData.data[playlistNodeIndex]
      playlist.data = {
        ...playlist.data,
        theme: {
          ...playlist?.data?.theme,
          ...data,
        },
      }
    }
    onChange(newValues)
    if (publish) {
      setPlaylistSettings(null)
    }
  }

  function handleSaveTrack(values) {
    updateTrack({ id: values.id, values }).then(() => setTracksToEdit([]))
  }

  function handleEditPlaylistTrack(trackId) {
    const track = tracks.find((item) => item.id === trackId)
    setTracksToEdit([track])
  }

  function handleTrackCancel(evt) {
    setTracksToEdit([])
    evt.stopPropagation()
  }

  function handleBulkSave(values) {
    updateTracks({ ids: tracksToEdit.map((track) => track.id), values }).then(
      () => {
        setTracksToEdit([])
      },
    )
  }

  return (
    <div className="portfolio-playlist-editor w-full h-full flex flex-col">
      <div className={clsx({ hidden: !isPreview })}>
        <div ref={ref}></div>
      </div>
      {!isPreview && (
        <>
          <div
            className={clsx('flex rounded-t py-2 px-6 items-center', {
              'bg-deep': theme === 'dark',
              'bg-white': theme === 'light',
            })}
          >
            <h5 className="mt-[-2px]">Player</h5>
            <PortfolioRegionSettings
              values={values}
              onChange={onChange}
              region="playlistRegion"
            />
            <div className="flex-1"></div>
            <div>
              <button
                className="flex-fill flex justify-center items-center"
                data-tooltip="Player Settings"
                onClick={() =>
                  setPlaylistSettings(playlistNode?.data?.theme || {})
                }
              >
                <GearIcon fill="#ccc" height="24" />
              </button>
            </div>
          </div>

          <div
            className={clsx(
              'flex-fill rounded-b bg-[rgba(0,0,0,0.05)] flex flex-col overflow-hidden',
            )}
            style={containerStyle}
          >
            <div className="flex h-full text-text-white bg-gray-dark">
              <div className="flex-fill max-w-[45%] bg-gray-light pb-4 overflow-auto max-h-[83vh]">
                <TracksView
                  onEditTracks={setTracksToEdit}
                  onModalClick={handleTrackCancel}
                  lockSelections={tracksToEdit.length > 0}
                />
              </div>
              <div className="flex-fill bg-gray-light pb-4 ml-4">
                <PlaylistEditor
                  values={values?.playlistData}
                  onChange={handlePlaylistChange}
                  isLoading={false}
                  isOutOfDate={false}
                  onEditTrack={handleEditPlaylistTrack}
                  mode="portfolio"
                />
              </div>
            </div>
          </div>
        </>
      )}

      {tracksToEdit.length === 1 && (
        <div className="fixed w-[calc(100%-60px)] flex h-full top-0 right-0 z-20">
          <button
            className="flex-1 bg-[#000000cc]"
            onClick={handleTrackCancel}
          ></button>
          <div className="h-full top-0 w-[60vw] min-w-[800px] right-0">
            <TrackView
              isLoading={updateTrackIsLoading}
              track={tracksToEdit[0]}
              onSubmit={handleSaveTrack}
              onCancel={handleTrackCancel}
            />
          </div>
        </div>
      )}
      {tracksToEdit.length > 1 && (
        <div className="fixed w-[calc(100%-60px)] flex h-full top-0 right-0 z-20">
          <button
            className="flex-1 bg-[#000000cc]"
            onClick={handleTrackCancel}
          ></button>
          <div className="h-full top-0 w-[60vw] min-w-[800px] right-0">
            <BulkTrackView
              isLoading={updateTracksIsLoading}
              tracks={tracksToEdit}
              onSubmit={handleBulkSave}
              onCancel={handleTrackCancel}
            />
          </div>
        </div>
      )}
      {!!playlistSettings && (
        <Modal
          isOpen={!!playlistSettings}
          onRequestClose={() => setPlaylistSettings(false)}
          contentLabel="Embeded Link Modal"
          className="modal w-[1115px] h-full animate-slide-out"
          overlayClassName="modal-overlay right"
        >
          <PlaylistEditModal
            values={playlistSettings}
            data={values.playlistData.data}
            id={playlistNode.id}
            onCloseClick={() => setPlaylistSettings(null)}
            onSubmit={handlePlaylistSettingsChange}
            mode="portfolio"
          />
        </Modal>
      )}
    </div>
  )
}

export default PortfolioPlaylistEditor
